import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api-client';
import { timesheetWorkedMilliseconds, timesheetIsOnLunch } from '../../../../shared/timesheet-work-time';

type CrewEntry = {id:string;technicianId:string;technicianName:string;clockIn:string;clockOut:string|null;lunchBreaksJson?:string|null};
const duration = (milliseconds:number) => {
  const seconds=Math.floor(Math.max(0,milliseconds)/1000);
  return [Math.floor(seconds/3600),Math.floor(seconds/60)%60,seconds%60].map(value=>String(value).padStart(2,'0')).join(':');
};

export function FieldJobTime({jobId,appointmentId,actingTechnicianId,crewCount,now,ownElapsedMs,running,completed}:{
  jobId:string;appointmentId:string;actingTechnicianId:string;crewCount:number;now:number;ownElapsedMs:number;running:boolean;completed:boolean;
}) {
  const [entries,setEntries]=useState<CrewEntry[]|null>(null);
  const [unavailable,setUnavailable]=useState(false);
  useEffect(()=>{
    const controller=new AbortController();
    let timer:ReturnType<typeof setTimeout>|undefined;
    setEntries(null);setUnavailable(false);
    const load=async()=>{
      try {
        const query=new URLSearchParams({appointmentId});
        if(actingTechnicianId)query.set('technicianId',actingTechnicianId);
        const response=await apiFetch(`/api/v1/workspace/field/jobs/${encodeURIComponent(jobId)}/crew-time?${query}`,{signal:controller.signal});
        const payload=await response.json();
        if(!response.ok||payload.data?.appointmentId!==appointmentId||!Array.isArray(payload.data?.items)||payload.data.truncated)throw new Error('Crew time unavailable');
        if(!controller.signal.aborted){setEntries(payload.data.items);setUnavailable(false);}
      } catch {
        if(!controller.signal.aborted){setEntries(null);setUnavailable(true);}
      } finally {
        if(!controller.signal.aborted&&crewCount>1)timer=setTimeout(()=>void load(),15000);
      }
    };
    void load();
    return()=>{controller.abort();if(timer)clearTimeout(timer);};
  },[jobId,appointmentId,actingTechnicianId,crewCount]);
  const technicians=new Map<string,{name:string;milliseconds:number;running:boolean}>();
  for(const entry of entries??[]){
    const previous=technicians.get(entry.technicianId);
    technicians.set(entry.technicianId,{name:entry.technicianName,milliseconds:(previous?.milliseconds??0)+timesheetWorkedMilliseconds(entry,now),running:Boolean(previous?.running||(!entry.clockOut&&!timesheetIsOnLunch(entry)))});
  }
  const crew=[...technicians.entries()];
  const combined=crew.reduce((sum,[,technician])=>sum+technician.milliseconds,0);
  return <div aria-label="Invoice job time" className="m-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-3">
    {crew.length>1 ? <>
      <p className="text-xs font-semibold text-emerald-900">Technician time · This visit</p>
      <ul className="mt-2 space-y-2">{crew.map(([id,technician])=><li key={id} className="flex items-center justify-between gap-3 text-sm"><span className="min-w-0"><span className="font-medium">{technician.name}</span><span className="ml-2 text-xs text-emerald-800">{technician.running?'Running':entries?.filter(entry=>entry.technicianId===id).every(entry=>entry.clockOut)?'Completed':'Paused'}</span></span><output aria-label={`${technician.name} job time`} className="shrink-0 font-semibold tabular-nums">{duration(technician.milliseconds)}</output></li>)}</ul>
      <div className="mt-3 flex items-center justify-between gap-3 border-t border-emerald-200 pt-3"><span className="text-sm font-semibold">Combined technician time</span><output aria-label="Combined job time" className="text-xl font-semibold tabular-nums text-emerald-900">{duration(combined)}</output></div>
    </> : <div className="flex items-center justify-between gap-3"><span className="text-xs text-emerald-900">{completed?'Total job time':running?'Job timer · Running':'Job time'}</span><output aria-label="Time on job" className="text-xl font-semibold tabular-nums text-emerald-900">{duration(ownElapsedMs)}</output></div>}
    {crewCount>1&&crew.length<2&&<p className="mt-2 text-xs text-emerald-900">{unavailable?'Crew time is unavailable. Showing your time only.':entries===null?'Loading crew time…':'No other technician time recorded yet.'}</p>}
  </div>;
}
